// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//       • Withdraw or disable access to the application or related services
//       • Remove hosted, cloud-based, or linked services under his control
//       • Revoke access to Build My Checks–related assets, code, APIs, or integrations
//
//  © 2025 Gareth Morgan — BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks – ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import PremiumReportRenderer from "../components/PremiumReportRenderer";
import webLogo from "../assets/image/logo_web.png";
import "../print.css";

export default function PrintView() {
  const [params] = useSearchParams();
  const ids = params.get("ids")?.split(",") || [];
  const companyId = params.get("company");
  const paramLogo = (() => {
    try {
      const raw = params.get("logo");
      return raw ? decodeURIComponent(raw) : null;
    } catch {
      return params.get("logo") || null;
    }
  })();
  const [companyLogo, setCompanyLogo] = useState(paramLogo || webLogo);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load company logo from Firestore if available
  useEffect(() => {
    if (!companyId) return;
    const loadLogo = async () => {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (companyDoc.exists() && companyDoc.data().logoURL) {
          setCompanyLogo(companyDoc.data().logoURL);
        }
      } catch (err) {
        console.warn('Could not load company logo:', err);
      }
    };
    loadLogo();
  }, [companyId]);

  // Normalize form definitions so sections have children mapped to fields
  const normalizeFields = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map((field) => {
        if (field.type === "section") {
          const childFields = field.fields || field.children || [];
          return { ...field, fields: normalizeFields(childFields) };
        }
        return field;
      });
    }
    if (typeof raw === "string") {
      try {
        return normalizeFields(JSON.parse(raw));
      } catch {
        return [];
      }
    }
    if (typeof raw === "object") {
      return Object.entries(raw).map(([k, v]) =>
        v && typeof v === "object" ? { id: v.id || k, ...v } : { id: k, question: String(v) }
      );
    }
    return [];
  };

  useEffect(() => {
    async function load() {
      const loaded = [];
      for (let id of ids) {
        try {
          const ref = doc(db, "companies", companyId, "submissions", id);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data();
            const companyForReport = data.companyId || companyId;
            // Also fetch form definition if available
            let formDef = [];
            if (data.formDef) {
              formDef = normalizeFields(data.formDef);
            } else if (data.formId) {
              try {
                const formRef = doc(db, "companies", companyForReport, "forms", data.formId);
                const formSnap = await getDoc(formRef);
                if (formSnap.exists()) {
                  formDef = normalizeFields(formSnap.data().fields);
                }
              } catch (err) {
                console.warn("Could not load form definition:", err);
              }
            }
            loaded.push({ 
              id, 
              ...data,
              formDef,
              title: data.formTitle || data.title || "Report",
              date: data.createdAt || data.submittedAt || data.date
            });
          }
        } catch (err) {
          console.error(`Failed to load report ${id}:`, err);
        }
      }
      setReports(loaded);
      setLoading(false);
    }
    load();
  }, [ids, companyId]);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h2>Loading reports for printing...</h2>
        <p>Please wait while we prepare {ids.length} report{ids.length > 1 ? 's' : ''}.</p>
      </div>
    );
  }

  return (
    <div className="print-container">
      <div className="print-actions" style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button
          onClick={() => window.print()}
          style={{ padding: "10px 14px", background: "#111827", color: "#fff", borderRadius: 8, border: "1px solid #1f2937", cursor: "pointer" }}
        >
          Print / Save PDF
        </button>
        <span style={{ marginLeft: 12, color: "#4b5563", alignSelf: "center" }}>or press Ctrl + P</span>
      </div>
      {reports.map((report, index) => (
        <div key={report.id} className="report-page">
          
          {/* LOGO + HEADER */}
          <div className="page-header">
            <img src={companyLogo} className="company-logo" alt="Company Logo" crossOrigin="anonymous" />
            <div>
              <h1>Report {index + 1} of {reports.length}</h1>
              <div className="report-subtitle">
                {report.title} - {new Date(report.date?.toDate?.() || report.date).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* CONTENT */}
          <div className="report-content">
            <PremiumReportRenderer
              formDef={report.formDef || []}
              responses={report.responses || report.answers || {}}
              changeLog={report.changeLog || []}
              remediation={report.remediation || {}}
              readOnly={true}
            />
          </div>

          {/* PAGE BREAK */}
          {index < reports.length - 1 && <div className="page-break" />}
        </div>
      ))}
      
      <div className="print-instructions">
        <p><strong>Ready to print!</strong> Press <kbd>Ctrl+P</kbd> (Windows) or <kbd>Cmd+P</kbd> (Mac) and choose "Save as PDF"</p>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
